import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";
import { resetAuthFailures } from "../lib/supabaseGuard";
import { toast } from "sonner";
import posthog from "posthog-js";

const AuthContext = createContext();

// ============================================================================
// GLOBAL AUTH STATE MANAGER (StrictMode & Double-Mount Resilient)
// ============================================================================
// React 18/19 mounts/unmounts components twice in development. This can cause
// parallel calls to getSession() and onAuthStateChange(), triggering local storage
// locks and deadlocks in the Supabase Client. We centralize the listener globally.
// ============================================================================

let globalSession = null;
let globalSessionLoaded = false;
let globalSessionPromise = null;
const authListeners = new Set();

function notifyListeners(event, session) {
  authListeners.forEach(listener => {
    try {
      listener(event, session);
    } catch (e) {
      console.error("[AuthContext] Error in auth listener callback:", e);
    }
  });
}

function initGlobalAuth() {
  if (globalSessionPromise) return;

  globalSessionPromise = supabase.auth.getSession()
    .then(({ data: { session } }) => {
      globalSession = session;
      globalSessionLoaded = true;
      notifyListeners('INITIAL_SESSION', session);
      return session;
    })
    .catch(err => {
      console.error("[AuthContext] Global initial session fetch error:", err);
      globalSession = null;
      globalSessionLoaded = true;
      notifyListeners('INITIAL_SESSION', null);
      return null;
    });

  supabase.auth.onAuthStateChange((event, session) => {
    // Treat INITIAL_SESSION as a normal session update but avoid double-firing if already loaded
    if (event === 'INITIAL_SESSION' && globalSessionLoaded) {
      return;
    }
    globalSession = session;
    globalSessionLoaded = true;
    notifyListeners(event, session);
  });
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // Explicit flag: true only once auth is fully initialized
  const [isAuthReady, setIsAuthReady] = useState(false);

  // Ref to avoid stale closure in onAuthStateChange listener
  const profileRef = useRef(null);
  const initialLoadHandled = useRef(false);

  const fetchProfile = useCallback(async (userId, retryCount = 0) => {
    // AbortController: if Supabase takes more than 8s to return the profile,
    // abort cleanly so the safety timer doesn't fire with user=null and
    // send the user to /login.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .abortSignal(controller.signal)
        .single();

      clearTimeout(timeoutId);

      if (error) {
        const code = (error.code || '').toString();
        const msg = (error.message || '').toLowerCase();

        // Transient network error — retry once after a short delay
        if (retryCount === 0 && (msg.includes('failed to fetch') || msg.includes('load failed') || msg.includes('aborterror'))) {
          console.warn('[AuthContext] Transient error fetching profile — retrying in 1s');
          await new Promise(r => setTimeout(r, 1000));
          return fetchProfile(userId, 1);
        }

        // RLS error — token might be stale, wait for refresh and retry once
        if (retryCount === 0 && (code === '42501' || error.status === 403)) {
          console.warn('[AuthContext] RLS error fetching profile — waiting for token refresh and retrying');
          await new Promise(r => setTimeout(r, 2000));
          return fetchProfile(userId, 1);
        }

        console.error('[AuthContext] fetchProfile error (attempt ' + (retryCount + 1) + '):', error.message);
        
        // Si el perfil no existe en absoluto (PGRST116), forzar cierre de sesión inmediato para limpiar la sesión inválida
        if (code === 'PGRST116') {
          console.warn('[AuthContext] Profile row not found (PGRST116). Force signing out to clean invalid local session.');
          supabase.auth.signOut().catch(() => {});
          setUser(null);
          setProfile(null);
          return;
        }

        // Si hay una falla RLS/Auth persistente después de reintentar, cerrar sesión automáticamente para evitar bloqueo
        if (retryCount > 0 && (code === '42501' || error.status === 403 || code === 'PGRST301' || error.status === 401)) {
          console.warn('[AuthContext] Persistent auth error on profile fetch. Force signing out to clean local storage.');
          supabase.auth.signOut().catch(() => {});
          setUser(null);
          setProfile(null);
        }
        return;
      }

      if (data) {
        let resolvedFmoId = data.fmo_id;

        // For fmo_admin without fmo_id set, look up the org they own
        if (!resolvedFmoId && data.role === 'fmo_admin') {
          try {
            const { data: fmoOrg } = await supabase
              .from('fmo_organizations')
              .select('id')
              .eq('owner_id', data.id)
              .single();
            if (fmoOrg) resolvedFmoId = fmoOrg.id;
          } catch (e) {
            console.warn('[AuthContext] Could not resolve FMO org:', e?.message);
          }
        }

        const effectiveProfile = {
          ...data,
          agency_id: data.agency_id || (data.role === 'agency_admin' ? data.id : null),
          fmo_id: resolvedFmoId,
        };
        // dhm_supervisor always reads from the DHM agency — no extra resolution needed
        // agency_id is already set on the profile row
        setProfile(effectiveProfile);
      }
    } catch (err) {
      clearTimeout(timeoutId);
      // Swallow AbortError (component unmount OR our own 8s timeout) silently
      if (err?.name === 'AbortError') return;
      console.warn('[AuthContext] fetchProfile error:', err?.message);
    }
  }, []);

  // Keep profileRef in sync with state for use in onAuthStateChange
  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);

  // Identify user in PostHog when both user and profile are available
  useEffect(() => {
    if (user && profile) {
      posthog.identify(user.id, {
        email: user.email,
        role: profile.role,
        first_name: profile.first_name,
        last_name: profile.last_name,
        agency_id: profile.agency_id,
        fmo_id: profile.fmo_id
      });
    }
  }, [user, profile]);

  // ================================================================
  // SINGLE SOURCE OF TRUTH: onAuthStateChange handles everything.
  // We fetch getSession() on mount as well to guarantee initial load,
  // using initialLoadHandled.current to prevent double profile queries.
  // ================================================================
  useEffect(() => {
    let mounted = true;

    // Safety timeout: if the global auth never resolves in 15s, unblock the UI.
    const safetyTimer = setTimeout(() => {
      if (mounted && !isAuthReady && !initialLoadHandled.current) {
        console.warn('[AuthContext] Safety timeout — auth listener did not fire in 15s');
        initialLoadHandled.current = true;
        setLoading(false);
        setIsAuthReady(true);
      }
    }, 15000);

    const handleInitialSession = async (session) => {
      if (initialLoadHandled.current) return;
      initialLoadHandled.current = true;
      clearTimeout(safetyTimer);

      if (session?.user) {
        setUser(session.user);
        await fetchProfile(session.user.id);
      }
      setLoading(false);
      setIsAuthReady(true);
    };

    const handleAuthEvent = async (event, session) => {
      if (!mounted) return;

      if (event === 'INITIAL_SESSION') {
        handleInitialSession(session);
        return;
      }

      if (event === 'TOKEN_REFRESHED') {
        resetAuthFailures();
        return;
      }

      if (event === 'SIGNED_OUT') {
        posthog.reset();
        setUser(null);
        setProfile(null);
        profileRef.current = null;
        setLoading(false);
        return;
      }

      // SIGNED_IN or other events with a session
      if (session?.user) {
        setUser(session.user);
        // Only re-fetch profile if we don't have one yet or user changed
        if (!profileRef.current || profileRef.current.id !== session.user.id) {
          await fetchProfile(session.user.id);
        }
      } else {
        setUser(null);
        setProfile(null);
      }
      setLoading(false);
    };

    // Register this instance's listener callback
    authListeners.add(handleAuthEvent);

    // Initialize global listener and initial session fetch
    initGlobalAuth();

    // If global load has already completed, initialize this instance immediately
    if (globalSessionLoaded) {
      handleInitialSession(globalSession);
    }

    return () => {
      mounted = false;
      clearTimeout(safetyTimer);
      authListeners.delete(handleAuthEvent);
    };
  }, [fetchProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (email, password) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      // Reset circuit breaker on successful login
      resetAuthFailures();

      if (data.user) {
        setUser(data.user);
        await fetchProfile(data.user.id);
      }

      return data;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email, password, metadata = {}) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
          emailRedirectTo: `${window.location.origin}/login?confirmed=true`
        }
      });
      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Public method for components to re-fetch the current profile
  // without a full page reload. Use after profile updates, agency changes, etc.
  const refreshProfile = useCallback(async () => {
    if (user?.id) {
      await fetchProfile(user.id);
    }
  }, [user?.id, fetchProfile]);

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      toast.success("Sesión cerrada");
    } catch (error) {
      toast.error("Error al cerrar sesión, pero se limpiará localmente.");
    } finally {
      posthog.reset();
      setUser(null);
      setProfile(null);
      resetAuthFailures();
      // Force a full reload to clear all React Context memory state and cached queries
      window.location.href = '/login';
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      login,
      signIn: login,
      signUp,
      logout,
      refreshProfile,
      loading,
      isAuthenticated: !!user,
      isAuthReady, // ← Components can check this before making queries
      isFMO: profile?.role === 'fmo_admin',
      isAgency: profile?.role === 'agency_admin',
      isAgent: profile?.role === 'agent',
      isSupervisor: profile?.role === 'dhm_supervisor',
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
